/**
 * Supabase Storage image-transform helpers.
 *
 * Public landing pages render user-uploaded photos straight from Supabase Storage. Originals can
 * be several MB each (a single carousel photo on a live page measured 12 MB), so shipping them at
 * full resolution makes pages load slowly. Supabase Storage exposes a `/render/image/` endpoint
 * that resizes + re-encodes on the fly (and serves WebP via `Accept` content negotiation), turning
 * that same 12 MB photo into ~300 KB. These helpers rewrite a public object URL to that endpoint.
 *
 * Non-Supabase URLs, non-public-object URLs, and vector/animated formats (SVG, GIF) are returned
 * unchanged — the transform endpoint only handles raster images and would 400 on an SVG.
 */

/** Public-object path segment in a Supabase Storage URL. */
const SUPABASE_OBJECT_PATH = "/storage/v1/object/public/";
/** On-the-fly image-transform path segment. */
const SUPABASE_RENDER_PATH = "/storage/v1/render/image/public/";
/** Formats the transform endpoint cannot process — returned as-is. */
const NON_TRANSFORMABLE = /\.(svg|gif)$/i;

export interface ImageTransformOptions {
  /** Target render width in px. Height scales proportionally. */
  width?: number;
  /** Output quality (20-100). Lower = smaller file. */
  quality?: number;
  /**
   * How the image fits the target box. Defaults to "contain", which preserves the original aspect
   * ratio. NOTE: passing only `width` without a resize mode makes the endpoint keep the original
   * height (distorting the image), so we always send a mode. CSS (`object-cover` /
   * `backgroundSize: cover`) handles any cropping at display time.
   */
  resize?: "cover" | "contain" | "fill";
}

/**
 * Rewrites a public Supabase Storage object URL to the `/render/image/` transform endpoint so the
 * CDN resizes/re-encodes the image instead of serving the full-resolution original.
 *
 * @param url - The source image URL (any origin)
 * @param options - Width / quality / resize transform parameters
 * @returns A transform-endpoint URL for eligible Supabase Storage images, otherwise the original URL unchanged
 */
export function transformStorageImageUrl(url: string, options: ImageTransformOptions = {}): string {
  try {
    const parsed = new URL(url);

    const isSupabaseObject = parsed.hostname.endsWith(".supabase.co") && parsed.pathname.includes(SUPABASE_OBJECT_PATH);
    if (!isSupabaseObject || NON_TRANSFORMABLE.test(parsed.pathname)) return url;

    parsed.pathname = parsed.pathname.replace(SUPABASE_OBJECT_PATH, SUPABASE_RENDER_PATH);
    if (options.width) parsed.searchParams.set("width", String(options.width));
    if (options.quality) parsed.searchParams.set("quality", String(options.quality));
    // Always send a resize mode — width-only requests keep the original height and distort the image.
    if (options.width) parsed.searchParams.set("resize", options.resize ?? "contain");

    return parsed.toString();
  } catch {
    return url;
  }
}
